import React from 'react';
import PropTypes from 'prop-types';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMHospitalityContainer';
import useStyles from '@front10/helpers/dist/UseStyles';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Loading from '../../others/Loading';
import Disclaimer from '../../others/Disclaimer';
import RateItemCard from '../../items/RateItemCard';
import SkeletonLowestRatesPropertiesMobile from '../../others/Skeleton/SkeletonLowestRatesPropertiesMobile';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';

//-------------------------------------
//Controls
//-------------------------------------
import ImagesCarousel from '../LowestRatesPropertiesContainer/atoms/ImageCarousel';
import HeaderContainer from '../../elements/HeaderContainer';
import Carousel from '../../controls/ElasticCarousel';
import InlineFilters from '../../controls/HospitalityInlineFilters/PropertyInlineFilter';
import SecondaryFilters from '../../controls/HospitalitySecondaryFilters/PropertySecondaryFilters';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getRates } from '../../../_integrations/everymundo/rates'; //PENDING
import { getPropertiesMultiAggregation } from '../../../_integrations/everymundo/locationsEM'; //PENDING
import { getResultsAggregationLength } from '../_commons/calendar';

//---------------------------------------
// Hooks
//---------------------------------------
import { useFilters } from '../_commons/hospitality/filters';
import useFiltersDisableSettings from '../_commons/hospitality/useFiltersDisabled';

const getRatesTags = () => {
  return new Promise((resolve, reject) => {
    resolve([]);
  });
};

const LowestRatesPropertiesContainerMobile = (props) => {
  const {
    prePopulation,
    integrationSettings,
    direction,
    translate,
    theme,
    showFilters,
    filters,
    loading,
    showSecondaryFilters,
    secondaryFilters,
    meta,
    pageContext,
    getProperties: getPropertiesFn,
    getRates: getRatesFn,
    initialData,
    minInitialResults,
    getRatesTags,
    faresTransformationLogic,
    ratesCarouselSettings,
    headerCarouselSettings,
    showDisclaimer = true,
    disclaimerContentSettings,
    disclaimer,
    skeletonSettings,
    showNoResultsPlaceholder = true,
    noResultsPlaceHolder,
    alert,
    alertInfoTextSettings,
    mainContentSettings,
    alertInfo
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  const {
    state,
    fares: { match, ...rates },
    dispatch,
    tracking,
    retry,
    isPropertySelected,
    ...filtersState
  } = useFilters({
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getProperties: getPropertiesFn,
      maxItemsAmount: null,
      nextMonth: false,
      defaultMonth: null
    },
    initialData,
    stayLengthData: secondaryFilters?.stayLengthSettings?.options,
    getFares: getRatesFn,
    getRatesTags, // ?? WHY
    faresTransformationLogic,
    minInitialResults,
    showNoResultsPlaceholder,
    availableFilters: showFilters ? filters.availableFilters : [],
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    enabledHistogram: true,
    getResultsLength: getResultsAggregationLength
  });

  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...secondaryFilters },
    {
      ratesTags: !isPropertySelected,
      stayLength: !isPropertySelected,
      budget: !isPropertySelected,
      dateRange: !isPropertySelected,
      travelerInterest: !isPropertySelected
    }
  );

  const renderAlertInfo = !isPropertySelected && (
    <Box dataTestId="no-rates-message" {...alertInfo}>
      <Text {...alertInfoTextSettings}>
        {translate(
          'cmp-lowest-rates-properties-alert-info',
          'Please provide a location to display associated properties lowest rates.'
        )}
      </Text>
    </Box>
  );

  return (
    <Box
      position="relative"
      className={`${LowestRatesPropertiesContainerMobile.componentName} ${className}`}
      style={myStyle}
    >
      {match('LOADING') && <Loading {...loading} enableAccessibilityMessage={true} />}
      {match('SKELETON') && (
        <SkeletonLowestRatesPropertiesMobile
          {...skeletonSettings}
          position="absolute"
          width="100"
        />
      )}
      <HeaderContainer
        defaultTitleValue={'Stay Your Way at Hyatt'}
        defaultSubtitleValue={'Lorem ipsum dolor sit amet, consectetur adipiscing elit.'}
        // handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {showFilters && <InlineFilters {...filters} {...filtersState} />}
      {showSecondaryFilters && (
        <SecondaryFilters
          {...filters}
          {...secondaryFilters}
          {...secondaryFiltersDisableSettings}
          {...filtersState}
          availableFilters={filtersState.availableSecondaryFilter}
          filter={{ ...{ prePopulation, integrationSettings, meta } }}
          loading={match('SKELETON')}
        />
      )}
      {renderAlertInfo}
      <Row flexDirection="column" dataTestId="rates-container" {...mainContentSettings}>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <Col>
            <NoFeaturedPopularFaresAlert dispatch={dispatch} {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('WAITING_FOR_PROPERTY') && (
          <Col textAlign="center">
            <Box {...noResultsPlaceHolder}></Box>
          </Col>
        )}
        {match('COMPLETE') && (
          <>
            {rates?.data &&
              rates.data.map(({ images, data: secondLevel }) => {
                const newRatesCarouselSettings = {
                  ...ratesCarouselSettings,
                  elementsToShow:
                    secondLevel.length === 1 ? 1 : ratesCarouselSettings.elementsToShow
                };
                return (
                  <>
                    <Col md={12}>
                      <Carousel
                        {...headerCarouselSettings}
                        ItemComponentInstance={ImagesCarousel}
                        itemsData={images}
                        tracking={tracking}
                      />
                    </Col>
                    <Col md={12}>
                      {secondLevel.length === 1 ? (
                        <RateItemCard
                          {...newRatesCarouselSettings.itemSettings}
                          data={secondLevel[0]}
                        />
                      ) : (
                        <Carousel
                          {...newRatesCarouselSettings}
                          ItemComponentInstance={RateItemCard}
                          itemsData={secondLevel}
                          tracking={tracking}
                        />
                      )}
                    </Col>
                  </>
                );
              })}
          </>
        )}
      </Row>
      {showDisclaimer && (
        <Row gutters {...disclaimerContentSettings}>
          <Col>
            <Disclaimer labelKey="cmp-lowest-rates-properties-disclaimer" {...disclaimer} />
          </Col>
        </Row>
      )}
    </Box>
  );
};

LowestRatesPropertiesContainerMobile.propTypes = {
  /**
   * Function for request to obtain propertys
   */
  getProperties: PropTypes.func,
  getHistograms: PropTypes.func
};
LowestRatesPropertiesContainerMobile.defaultProps = {
  getProperties: getPropertiesMultiAggregation,
  getRates,
  getRatesTags
};

LowestRatesPropertiesContainerMobile.componentName = 'LowestRatesPropertiesContainerMobile';

export default withCommonEMContainer(LowestRatesPropertiesContainerMobile);
